import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, X } from "lucide-react";
import { CWS_URL } from "@/components/SiteChrome";

const parseCsv = (text) => {
  const rows = []; let cur = "", row = [], q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; }
    else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n") { row.push(cur); rows.push(row); row = []; cur = ""; }
    else if (c !== "\r") cur += c;
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.slice(1).filter((r) => r.length >= 2 && r[0]).map((r) => ({ term: r[0], definition: r[1].replace(/ — Source:.*$/, "") }));
};

const STATUS = {
  confirmed: { icon: CheckCircle2, cls: "bg-emerald-600", title: "Confirmed by GlossLink" },
  noext: { icon: AlertTriangle, cls: "bg-amber-600", title: "GlossLink isn't running in this browser" },
  unknown: { icon: AlertTriangle, cls: "bg-slate-700", title: "Sent to GlossLink — no confirmation yet" },
};

export const AddConfirmBar = () => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = (e) => { setInfo(e.detail); setOpen(false); };
    window.addEventListener("glosspack-added", on);
    return () => window.removeEventListener("glosspack-added", on);
  }, []);
  if (!info) return null;
  const S = STATUS[info.status];
  const terms = parseCsv(info.csv);
  return (
    <div className="fixed inset-x-0 top-0 z-50 shadow-xl" data-testid="add-confirm-bar">
      <div className={`${S.cls} text-white`}>
        <div className="mx-auto max-w-6xl px-6 py-3 sm:pr-[380px]">
          <div className="flex items-start gap-3">
            <S.icon className="mt-0.5 h-6 w-6 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-extrabold" data-testid="add-confirm-title">{S.title} · {info.name}</div>
              <div className="text-sm text-white/90" data-testid="add-confirm-message">
                {info.status === "confirmed" && <>GlossLink says: <i>“{info.extMessage}”</i> Open the GlossLink panel and pick <b>“{info.name}”</b> (or “All Terms”) in the glossary dropdown — then these words highlight on every page you open, and each definition ends with “Source: …” naming the book.</>}
                {info.status === "noext" && <>We sent the pack, but GlossLink isn't active on this tab. Most common cause: this tab was opened before GlossLink was installed or updated — <button onClick={() => window.location.reload()} className="underline font-semibold" data-testid="add-confirm-reload">reload this page</button> and click Add again. Still nothing? Open <code className="rounded bg-white/20 px-1">chrome://extensions</code> → GlossLink → Details → set “Site access” to <b>On all sites</b>, make sure it's turned on, then reload. Not installed yet? <a href={CWS_URL} target="_blank" rel="noopener noreferrer" className="underline font-semibold" data-testid="add-confirm-install">Install GlossLink</a>.</>}
                {info.status === "unknown" && <>The pack was sent. Click the GlossLink icon in your toolbar — “{info.name}” should be in the glossary list. If it isn't, install or enable GlossLink and click Add again.</>}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={() => setOpen(!open)} className="rounded-lg bg-white/15 px-3 py-1.5 text-sm font-semibold hover:bg-white/25" data-testid="add-confirm-toggle">
                  {open ? <ChevronUp className="inline h-4 w-4" /> : <ChevronDown className="inline h-4 w-4" />} {open ? "Hide" : "Show"} the {terms.length} words &amp; definitions
                </button>
                <button onClick={() => setInfo(null)} className="rounded-lg bg-white/15 px-3 py-1.5 text-sm font-semibold hover:bg-white/25" data-testid="add-confirm-close"><X className="inline h-4 w-4" /> Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {open && (
        <div className="max-h-[55vh] overflow-y-auto border-b border-slate-200 bg-white" data-testid="add-confirm-list">
          <div className="mx-auto max-w-6xl px-6 py-4">
            <p className="mb-3 text-xs text-slate-500">Everything below was sent to your GlossLink. {info.status === "confirmed" && /free plan|20 terms/i.test(info.extMessage) && <b>On the free plan the first 20 are defined; the rest unlock with Pro.</b>}</p>
            <ol className="divide-y divide-slate-100">
              {terms.map((t, i) => (
                <li key={i} className="grid gap-1 py-2 sm:grid-cols-[220px_1fr]" data-testid="add-confirm-term">
                  <span className="text-sm font-bold text-slate-900">{i + 1}. <mark className="rounded-sm bg-yellow-200 px-1">{t.term}</mark></span>
                  <span className="font-reading text-sm text-slate-700">{t.definition}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};
